import {
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  TooltipTrigger,
  TooltipContent,
  DateFormatted,
  DropdownMenu,
  CardContent,
  CardFooter,
  CardHeader,
  Typography,
  CardTitle,
  Loading,
  Tooltip,
  Button,
  Anchor,
  Card,
} from '@ui/components';
import { MenuVertical, NavDocuments, Report } from '@ui/components/icons';
import { ComponentPropsWithoutRef, FC } from 'react';
import { InvoiceType } from '@interfaces/types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Routes } from '@shared/enums';
import { cn } from '@ui/utils';

import { useCostCenterInvoices } from './use-cost-center-invoices';

export const CostCenterInvoices: FC<ComponentPropsWithoutRef<typeof Card>> = ({
  className,
  ...props
}) => {
  const { t } = useTranslation();
  const { isDownloadingFile, costCenterId, downloadFile, companyId, isLoading, invoices } =
    useCostCenterInvoices();

  return (
    <Card className={cn('flex flex-col', className)} {...props}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <NavDocuments className="size-5 text-weak" />
          <CardTitle>{t('COST_CENTER.INVOICES.TITLE')}</CardTitle>
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-auto">
        <Loading isLoading={isLoading} className="h-full">
          {invoices.length > 0 ? (
            <div className="flex flex-col">
              {invoices.map((invoice) => (
                <div
                  className="flex items-center justify-between border-b border-border-weak py-3 group"
                  key={invoice.id}
                >
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 ">
                    {/* TODO: change to invoice type from be when it's ready */}
                    <div
                      className={cn(
                        'flex items-center justify-center row-span-2 rounded-full bg-surface-weak py-2 size-8',
                        invoice.type === InvoiceType.INVOICE
                          ? 'bg-success/10 text-success'
                          : 'bg-blue/10 text-blue',
                      )}
                    >
                      <Report className="size-4" />
                    </div>
                    <Typography className="truncate" variant="h100">
                      {invoice.name}
                    </Typography>
                    <Typography variant="labelSmallBook" className="text-weak">
                      <DateFormatted date={new Date(invoice.date)} format="DATE_SHORT" />
                    </Typography>
                  </div>

                  <DropdownMenu>
                    <Tooltip>
                      <TooltipContent>{t('COST_CENTER.INVOICES.MORE')}</TooltipContent>
                      <DropdownMenuTrigger asChild>
                        <TooltipTrigger asChild>
                          <Button
                            className="collapse group-hover:visible data-[state=open]:visible"
                            disabled={isDownloadingFile}
                            variant="ghost"
                            size="small"
                            icon
                          >
                            <MenuVertical />
                          </Button>
                        </TooltipTrigger>
                      </DropdownMenuTrigger>
                    </Tooltip>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          downloadFile({
                            objectName: invoice.objectName!,
                            bucketName: invoice.bucketName!,
                          })
                        }
                        disabled={isDownloadingFile}
                      >
                        <Typography variant="labelMediumMedium">
                          {t('COST_CENTER.INVOICES.DOWNLOAD')}
                        </Typography>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <Typography variant="labelMediumMedium">{t('COST_CENTER.INVOICES.EMPTY')}</Typography>
            </div>
          )}
        </Loading>
      </CardContent>

      {!!costCenterId && (
        <CardFooter>
          <Anchor disabled={isLoading} asChild>
            <Link
              to={`/${companyId}/${Routes.FILES_REPORTS}/${Routes.INVOICING}`}
              className="w-full"
            >
              <Button disabled={isLoading} variant="link" wide>
                {t('COST_CENTER.INVOICES.VIEW_ALL')}
              </Button>
            </Link>
          </Anchor>
        </CardFooter>
      )}
    </Card>
  );
};
